import React from 'react'
import axios from 'axios'
import {
    addDays,
    addWeeks,
    format,
    parseISO,
    startOfWeek,
} from 'date-fns'
import {
    MdAutoAwesome,
    MdCheckCircle,
    MdDirectionsCar,
    MdEvent,
    MdLocationOn,
    MdLockOutline,
    MdMusicNote,
    MdPerson,
    MdRefresh,
    MdSchedule,
    MdWarning,
} from 'react-icons/md'
import { getRequestHeaders } from '../../api'
import { baseUrl } from '../../assets/Animations'
import { getTeacherEventsForWeek } from '../../Calendar/calendarFunctions'
import {
    calculatePlacementRouteDetails,
    collectRequiredDrivePairs,
    optimizeWeeklySchedule,
} from '../../Scheduling2025/scheduleOptimizer'
import {
    DRIVE_TIME_BUCKET,
    formatFamilyAddress,
    resolveFamilyPlaceKey,
} from './summerBreakDriveTimeUtils'
import {
    buildAvailableTeacherGroups,
    buildLockedCalendarEvents,
    buildPlannerStudents,
    buildPlannerTeachers,
    createDriveTimeLookup,
    DAY_NAMES,
    formatMinutesAsTime,
    getAvailableTeachersForSchool,
    getReplacementStudentsForSchool,
    resolveTeacherPlaceKey,
} from './summerBreakReplacementPlannerUtils'
import './summer-break-replacement-planner.css'

const getDefaultWeekValue = () => format(
    startOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 }),
    'yyyy-MM-dd'
)

const responseArray = (response, property) => {
    if (Array.isArray(response?.data?.[property])) return response.data[property]
    return Array.isArray(response?.data) ? response.data : []
}

const normalize = (value) => String(value || '').trim().toLowerCase()

const teacherInitials = (name) => String(name || 'Teacher')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()

const getExternalEventKey = (event, index) => {
    const teacherId = event?.teacherId || event?.TeacherID || event?.teacherID || 'teacher'
    const eventId = event?.id || event?._id || event?.lessonId || index
    return `calendar-${teacherId}-${eventId}`
}

const findDirectoryByKey = (directories, key) => (
    (directories || []).find((directory) => normalize(directory?.key) === normalize(key))
)

const resolveCalendarEventPlaceKey = (event, directories, index) => {
    const explicitKey = event?.placeKey || event?.place_key || event?.placeId
    if (explicitKey) return String(explicitKey)

    const familyId = String(event?.familyId || event?.FamilyID || '').trim()
    const familyKey = familyId
        ? resolveFamilyPlaceKey({ familyId }, directories)
        : ''
    if (familyKey) return familyKey

    const externalKey = getExternalEventKey(event, index)
    return findDirectoryByKey(directories, externalKey)?.key || ''
}

const locationLabel = (item) => item?.studentName
    || item?.familyName
    || item?.Name
    || item?.name
    || 'Saved calendar lesson'

const buildMissingLocationPlan = ({
    teachers,
    students,
    calendarEvents,
    directories,
}) => {
    const entries = new Map()
    const blockers = new Map()
    const addLocation = ({ key, address, label, roles, teacherIds, familyIds }) => {
        if (!key) return
        if (entries.has(String(key))) return
        if (!address) {
            blockers.set(String(key), label)
            return
        }
        blockers.delete(String(key))
        entries.set(String(key), {
            key: String(key),
            address,
            label,
            roles,
            teacherIds,
            familyIds,
        })
    }

    teachers.forEach((teacher) => {
        if (resolveTeacherPlaceKey(teacher, directories)) return
        const teacherId = String(teacher?.TeacherID || '').trim()
        addLocation({
            key: teacherId,
            address: formatFamilyAddress(teacher?.Address || teacher?.address),
            label: teacher?.Name || teacherId,
            roles: ['teacher'],
            teacherIds: teacherId ? [teacherId] : [],
            familyIds: [],
        })
    })

    students.forEach((student) => {
        if (resolveFamilyPlaceKey(student, directories)) return
        const familyId = String(student?.familyId || '').trim()
        addLocation({
            key: familyId,
            address: formatFamilyAddress(student?.familyAddress),
            label: student?.familyName || student?.studentName || familyId,
            roles: ['family'],
            teacherIds: [],
            familyIds: familyId ? [familyId] : [],
        })
    })

    calendarEvents.forEach((event, index) => {
        if (resolveCalendarEventPlaceKey(event, directories, index)) return
        const familyId = String(event?.familyId || event?.FamilyID || '').trim()
        const key = familyId || getExternalEventKey(event, index)
        addLocation({
            key,
            address: formatFamilyAddress(
                event?.familyAddress || event?.address || event?.locationAddress
            ),
            label: locationLabel(event),
            roles: familyId ? ['family'] : ['calendar'],
            teacherIds: [],
            familyIds: familyId ? [familyId] : [],
        })
    })

    return {
        entries: Array.from(entries.values()),
        blockers: Array.from(blockers.values()),
    }
}

const instrumentNames = (student) => (
    (student?.instrumentNames || [])
        .filter(Boolean)
        .join(', ')
        || 'Instrument not listed'
)

const SummerBreakReplacementPlanner = ({ summary }) => {
    const schoolOptions = React.useMemo(
        () => (summary?.schools || []).filter((school) => school?.needsNewTeacher > 0),
        [summary]
    )
    const [selectedSchoolId, setSelectedSchoolId] = React.useState('')
    const [weekValue, setWeekValue] = React.useState(getDefaultWeekValue)
    const [maxDriveValue, setMaxDriveValue] = React.useState('15')
    const [status, setStatus] = React.useState('idle')
    const [error, setError] = React.useState('')
    const [result, setResult] = React.useState(null)
    const [previewContext, setPreviewContext] = React.useState(null)
    const [missingLocations, setMissingLocations] = React.useState({ entries: [], blockers: [] })
    const [missingPairs, setMissingPairs] = React.useState([])

    const effectiveSchoolId = selectedSchoolId || schoolOptions[0]?.id || ''
    const replacementStudents = React.useMemo(
        () => getReplacementStudentsForSchool(summary, effectiveSchoolId),
        [effectiveSchoolId, summary]
    )
    const availableTeachers = React.useMemo(
        () => getAvailableTeachersForSchool(summary, effectiveSchoolId),
        [effectiveSchoolId, summary]
    )
    const teacherGroups = React.useMemo(
        () => buildAvailableTeacherGroups(summary, effectiveSchoolId),
        [effectiveSchoolId, summary]
    )

    const resetPreview = () => {
        setStatus('idle')
        setError('')
        setResult(null)
        setPreviewContext(null)
        setMissingLocations({ entries: [], blockers: [] })
        setMissingPairs([])
    }

    const generatePreview = async () => {
        if (!effectiveSchoolId || replacementStudents.length === 0) return
        if (availableTeachers.length === 0) {
            setStatus('error')
            setError('No active teacher at this school is accepting students with a matching instrument and saved availability.')
            return
        }

        setStatus('loading')
        setError('')
        setResult(null)
        setMissingLocations({ entries: [], blockers: [] })
        setMissingPairs([])

        try {
            const weekStart = startOfWeek(parseISO(weekValue), { weekStartsOn: 1 })
            const maxDriveMinutes = Math.max(1, Number(maxDriveValue) || 15)
            const teacherIds = availableTeachers.map((teacher) => String(teacher.TeacherID))
            const [directoryResponse, edgeResponse, ...calendarResponses] = await Promise.all([
                axios.get(`${baseUrl}/place-directory/${effectiveSchoolId}`, getRequestHeaders()),
                axios.get(`${baseUrl}/drive-edges/${effectiveSchoolId}`, getRequestHeaders()),
                ...teacherIds.map((teacherId) => (
                    axios.get(`${baseUrl}/calendar/teacher/${teacherId}`, getRequestHeaders())
                )),
            ])
            const directories = responseArray(directoryResponse, 'directories')
            const edges = responseArray(edgeResponse, 'edges')
            const rawEvents = calendarResponses.flatMap((response) => responseArray(response, 'events'))
            const concreteEvents = getTeacherEventsForWeek(rawEvents, teacherIds, weekStart)
            const locationPlan = buildMissingLocationPlan({
                teachers: availableTeachers,
                students: replacementStudents,
                calendarEvents: concreteEvents,
                directories,
            })
            const context = {
                schoolId: effectiveSchoolId,
                weekStart,
                teachers: availableTeachers,
                students: replacementStudents,
                concreteEvents,
                directories,
                edges,
                maxDriveMinutes,
            }
            setPreviewContext(context)

            if (locationPlan.entries.length > 0 || locationPlan.blockers.length > 0) {
                setMissingLocations(locationPlan)
                setStatus('needs-locations')
                return
            }

            const calendarEventsWithPlaces = concreteEvents.map((event, index) => ({
                ...event,
                placeKey: resolveCalendarEventPlaceKey(event, directories, index),
            }))
            const plannerTeachers = buildPlannerTeachers(availableTeachers, directories)
            const plannerStudents = buildPlannerStudents(replacementStudents, directories)
            const fixedEvents = buildLockedCalendarEvents({
                events: calendarEventsWithPlaces,
                weekStart,
                teacherIds,
                directories,
            })
            const unresolvedFixedEvents = fixedEvents.filter((event) => !event.placeKey)
            if (unresolvedFixedEvents.length > 0) {
                setMissingLocations({
                    entries: [],
                    blockers: unresolvedFixedEvents.map(() => 'An existing calendar lesson has no usable location'),
                })
                setStatus('needs-locations')
                return
            }

            const travelMinutes = createDriveTimeLookup(edges)
            const requiredPairs = collectRequiredDrivePairs({
                teachers: plannerTeachers,
                students: plannerStudents,
                fixedEvents,
                travelMinutes,
                paddingMinutes: 0,
                maxDriveMinutes,
            })
            if (requiredPairs.length > 0) {
                setMissingPairs(requiredPairs)
                setPreviewContext({ ...context, fixedEvents })
                setStatus('needs-drive')
                return
            }

            const optimizationResult = optimizeWeeklySchedule({
                teachers: plannerTeachers,
                students: plannerStudents,
                fixedEvents,
                travelMinutes,
                slotMinutes: 5,
                paddingMinutes: 0,
                maxDriveMinutes,
            })
            setPreviewContext({ ...context, fixedEvents })
            setResult(optimizationResult)
            setStatus('complete')
        } catch (requestError) {
            console.error('Unable to generate summer break replacement preview:', requestError)
            setStatus('error')
            setError('The preview could not be generated because one or more teacher calendars or cached travel records could not be loaded. Nothing was changed.')
        }
    }

    const prepareLocationData = async () => {
        if (!previewContext || missingLocations.entries.length === 0) return
        setStatus('preparing-locations')
        setError('')
        try {
            await axios.post(
                `${baseUrl}/place-directories/bulk`,
                {
                    schoolId: previewContext.schoolId,
                    directories: missingLocations.entries,
                },
                getRequestHeaders()
            )
            await generatePreview()
        } catch (requestError) {
            console.error('Unable to prepare summer break planner locations:', requestError)
            setStatus('needs-locations')
            setError('The saved addresses could not be prepared for routing. No calendar data was changed.')
        }
    }

    const calculateTravelData = async () => {
        if (!previewContext || missingPairs.length === 0) return
        setStatus('calculating-drive')
        setError('')
        try {
            await axios.post(
                `${baseUrl}/drive-edges/fill`,
                {
                    schoolId: previewContext.schoolId,
                    bucket: DRIVE_TIME_BUCKET,
                    todBucket: DRIVE_TIME_BUCKET,
                    pairs: missingPairs,
                },
                getRequestHeaders()
            )
            await generatePreview()
        } catch (requestError) {
            console.error('Unable to calculate summer break planner travel data:', requestError)
            setStatus('needs-drive')
            setError('The missing travel estimates could not be calculated. No calendar data was changed.')
        }
    }

    const studentByKey = new Map(
        replacementStudents.map((student) => [`${student.familyId}-${student.studentId}`, student])
    )
    const teacherById = new Map(
        availableTeachers.map((teacher) => [String(teacher.TeacherID), teacher])
    )
    const directoryLabelByKey = new Map(
        (previewContext?.directories || []).map((directory) => [
            normalize(directory?.key),
            directory?.label || 'Location',
        ])
    )
    const routeDetails = result && previewContext
        ? calculatePlacementRouteDetails({
            teachers: buildPlannerTeachers(
                previewContext.teachers,
                previewContext.directories
            ),
            fixedEvents: previewContext.fixedEvents || [],
            placements: result.placements || [],
            travelMinutes: createDriveTimeLookup(previewContext.edges),
            paddingMinutes: 0,
            maxDriveMinutes: previewContext.maxDriveMinutes,
        })
        : []
    const routeDetailsByStudent = new Map(
        routeDetails.map((details) => [details.studentKey, details])
    )
    const getRouteStopName = (stop, teacher) => {
        if (!stop) return 'route'
        if (stop.boundary) return `${teacher?.Name || 'Teacher'} home`
        const student = studentByKey.get(stop.studentKey)
        if (student?.studentName) return student.studentName
        return stop.studentName
            || stop.familyName
            || stop.label
            || directoryLabelByKey.get(normalize(stop.placeKey))
            || 'locked lesson'
    }
    const placementGroups = (result?.placements || []).reduce((groups, placement) => {
        const key = `${placement.teacherId}:${placement.dayIndex}`
        if (!groups[key]) {
            groups[key] = {
                key,
                teacher: teacherById.get(String(placement.teacherId)),
                dayIndex: placement.dayIndex,
                placements: [],
            }
        }
        groups[key].placements.push(placement)
        return groups
    }, {})
    const sortedPlacementGroups = Object.values(placementGroups).sort((left, right) => (
        String(left.teacher?.Name || '').localeCompare(String(right.teacher?.Name || ''))
        || left.dayIndex - right.dayIndex
    ))

    if (schoolOptions.length === 0) {
        return (
            <section className='summer-break-planner'>
                <div className='summer-break-planner__heading'>
                    <MdAutoAwesome size={21} aria-hidden='true' />
                    <div>
                        <h3>New-teacher schedule preview</h3>
                        <p>No summer-break students currently need a new teacher.</p>
                    </div>
                </div>
            </section>
        )
    }

    const isBusy = ['loading', 'preparing-locations', 'calculating-drive'].includes(status)

    return (
        <section className='summer-break-planner'>
            <div className='summer-break-planner__heading-row'>
                <div className='summer-break-planner__heading'>
                    <MdAutoAwesome size={21} aria-hidden='true' />
                    <div>
                        <h3>New-teacher schedule preview</h3>
                        <p>See available teachers and suggested openings for students who need a replacement.</p>
                    </div>
                </div>
                <div className='summer-break-planner__readonly'>
                    <MdLockOutline size={14} aria-hidden='true' />
                    Preview only
                </div>
            </div>

            <div className='summer-break-planner__safety-note'>
                <MdLockOutline size={17} aria-hidden='true' />
                <span>
                    <strong>Existing lessons are locked.</strong> This tool reads the selected week and only suggests where replacement students could fit. It cannot save, move, or delete calendar events.
                </span>
            </div>

            <div className='summer-break-planner__controls'>
                <label>
                    School
                    <select
                        value={effectiveSchoolId}
                        onChange={(event) => {
                            setSelectedSchoolId(event.target.value)
                            resetPreview()
                        }}
                    >
                        {schoolOptions.map((school) => (
                            <option key={school.id} value={school.id}>{school.name}</option>
                        ))}
                    </select>
                </label>
                <label>
                    Preview week
                    <span className='summer-break-planner__date-input'>
                        <MdEvent size={16} aria-hidden='true' />
                        <input
                            type='date'
                            value={weekValue}
                            onChange={(event) => {
                                if (!event.target.value) return
                                const monday = format(
                                    startOfWeek(parseISO(event.target.value), { weekStartsOn: 1 }),
                                    'yyyy-MM-dd'
                                )
                                setWeekValue(monday)
                                resetPreview()
                            }}
                            aria-label='Summer break schedule preview week'
                        />
                    </span>
                </label>
                <label>
                    Max drive / leg
                    <span className='summer-break-planner__number-input'>
                        <MdDirectionsCar size={16} aria-hidden='true' />
                        <input
                            type='number'
                            min='1'
                            max='180'
                            step='1'
                            value={maxDriveValue}
                            onChange={(event) => {
                                setMaxDriveValue(event.target.value)
                                resetPreview()
                            }}
                            onBlur={() => {
                                if (!Number(maxDriveValue)) setMaxDriveValue('15')
                            }}
                            aria-label='Maximum proposed drive time per leg in minutes'
                        />
                        <span>min</span>
                    </span>
                </label>
                <button
                    type='button'
                    className='summer-break-planner__generate'
                    onClick={generatePreview}
                    disabled={isBusy}
                >
                    {isBusy ? <span className='summer-break-planner__spinner' /> : <MdAutoAwesome size={17} />}
                    {status === 'loading' ? 'Reading calendars…' : 'Generate preview'}
                </button>
            </div>

            <div className='summer-break-planner__teacher-section'>
                <div className='summer-break-planner__subheading'>
                    <div>
                        <MdPerson size={17} aria-hidden='true' />
                        <strong>Available teachers</strong>
                    </div>
                    <span>{availableTeachers.length} for {replacementStudents.length} students</span>
                </div>
                <div className='summer-break-planner__instrument-groups'>
                    {teacherGroups.map((group) => (
                        <div className='summer-break-planner__instrument-group' key={group.instrumentName}>
                            <div className='summer-break-planner__instrument-label'>
                                <MdMusicNote size={14} aria-hidden='true' />
                                <strong>{group.instrumentName}</strong>
                                <span>{group.students.length} need placement</span>
                            </div>
                            {group.teachers.length > 0 ? (
                                <div className='summer-break-planner__teachers'>
                                    {group.teachers.map((teacher) => (
                                        <div className='summer-break-planner__teacher' key={teacher.TeacherID}>
                                            {teacher.ImageName ? (
                                                <img
                                                    src={`https://app.atharo.app/images/${teacher.ImageName}`}
                                                    alt=''
                                                />
                                            ) : (
                                                <span className='summer-break-planner__teacher-initials'>
                                                    {teacherInitials(teacher.Name)}
                                                </span>
                                            )}
                                            <span>
                                                <strong>{teacher.Name}</strong>
                                                <small>{teacher.availabilityDays.join(', ')}</small>
                                            </span>
                                            <span className='summer-break-planner__match-count'>
                                                {teacher.matchingStudentIds.length} match{teacher.matchingStudentIds.length === 1 ? '' : 'es'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className='summer-break-planner__no-teacher'>No eligible teacher found</div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {error && (
                <div className='summer-break-planner__message is-error' role='alert'>
                    <MdWarning size={18} aria-hidden='true' />
                    <span>{error}</span>
                </div>
            )}

            {status === 'needs-locations' && (
                <div className='summer-break-planner__message is-warning'>
                    <MdLocationOn size={19} aria-hidden='true' />
                    <div>
                        <strong>Location data is needed before a safe preview can be made.</strong>
                        {missingLocations.entries.length > 0 && (
                            <p>{missingLocations.entries.length} saved address{missingLocations.entries.length === 1 ? '' : 'es'} can be prepared for routing.</p>
                        )}
                        {missingLocations.blockers.length > 0 && (
                            <p>Add a saved address for: {Array.from(new Set(missingLocations.blockers)).join(', ')}.</p>
                        )}
                        {missingLocations.entries.length > 0 && (
                            <button type='button' onClick={prepareLocationData}>
                                <MdLocationOn size={16} /> Prepare location data
                            </button>
                        )}
                    </div>
                </div>
            )}

            {status === 'needs-drive' && (
                <div className='summer-break-planner__message is-warning'>
                    <MdDirectionsCar size={19} aria-hidden='true' />
                    <div>
                        <strong>{missingPairs.length} cached travel estimate{missingPairs.length === 1 ? ' is' : 's are'} missing.</strong>
                        <p>Calculate only those routes, then the preview can place students safely.</p>
                        <button type='button' onClick={calculateTravelData}>
                            <MdDirectionsCar size={16} /> Calculate missing travel data
                        </button>
                    </div>
                </div>
            )}

            {status === 'complete' && result && (
                <div className='summer-break-planner__results'>
                    <div className='summer-break-planner__result-summary'>
                        <div>
                            <MdCheckCircle size={20} aria-hidden='true' />
                            <span>
                                <strong>{result.metrics?.scheduledCount || 0} of {result.metrics?.totalStudents || replacementStudents.length} placed</strong>
                                <small>Suggested placements only</small>
                            </span>
                        </div>
                        <div>
                            <MdDirectionsCar size={18} aria-hidden='true' />
                            <span>
                                <strong>+{Math.max(0, Math.round(result.metrics?.addedDriveMinutes || 0))} min</strong>
                                <small>Added route drive</small>
                            </span>
                        </div>
                        <div>
                            <MdLockOutline size={18} aria-hidden='true' />
                            <span>
                                <strong>{previewContext?.fixedEvents?.length || 0} locked</strong>
                                <small>Existing lessons</small>
                            </span>
                        </div>
                        <div>
                            <MdDirectionsCar size={18} aria-hidden='true' />
                            <span>
                                <strong>{previewContext?.maxDriveMinutes || 15} min max</strong>
                                <small>Per proposed drive leg</small>
                            </span>
                        </div>
                    </div>
                    <div className='summer-break-planner__route-key'>
                        <MdDirectionsCar size={14} aria-hidden='true' />
                        <span>
                            Route legs are recalculated from the completed schedule. “Final route” is the change if that lesson were removed; shared legs mean those individual values are not intended to add up to the total above.
                        </span>
                    </div>

                    {sortedPlacementGroups.length > 0 ? (
                        <div className='summer-break-planner__schedule'>
                            {sortedPlacementGroups.map((group) => (
                                <article key={group.key}>
                                    <header>
                                        <span className='summer-break-planner__teacher-initials'>
                                            {teacherInitials(group.teacher?.Name)}
                                        </span>
                                        <span>
                                            <strong>{group.teacher?.Name || 'Teacher'}</strong>
                                            <small>
                                                {DAY_NAMES[group.dayIndex]} · {format(addDays(previewContext.weekStart, group.dayIndex), 'MMM d')}
                                            </small>
                                        </span>
                                    </header>
                                    <div>
                                        {group.placements
                                            .slice()
                                            .sort((left, right) => left.startMinutes - right.startMinutes)
                                            .map((placement) => {
                                                const student = studentByKey.get(placement.studentKey)
                                                const route = routeDetailsByStudent.get(placement.studentKey)
                                                const previousName = getRouteStopName(route?.previous, group.teacher)
                                                const nextName = getRouteStopName(route?.next, group.teacher)
                                                const marginalLabel = Number.isFinite(route?.marginalDrive)
                                                    ? `${route.marginalDrive >= 0 ? '+' : ''}${Math.round(route.marginalDrive)}m final route`
                                                    : 'Route unavailable'
                                                return (
                                                    <div className='summer-break-planner__placement' key={placement.studentKey}>
                                                        <MdSchedule size={16} aria-hidden='true' />
                                                        <span>
                                                            <strong>{student?.studentName || placement.studentKey}</strong>
                                                            <small>{instrumentNames(student)} · {student?.familyName || 'Family'}</small>
                                                        </span>
                                                        <span className='summer-break-planner__placement-time'>
                                                            {formatMinutesAsTime(placement.startMinutes)}–{formatMinutesAsTime(placement.endMinutes)}
                                                            <small>{marginalLabel}</small>
                                                        </span>
                                                        <span className='summer-break-planner__route-legs'>
                                                            <small>
                                                                {Number.isFinite(route?.driveFromPrevious) ? `${route.driveFromPrevious}m` : '—'} from {previousName}
                                                            </small>
                                                            <small>
                                                                {Number.isFinite(route?.driveToNext) ? `${route.driveToNext}m` : '—'} to {nextName}
                                                            </small>
                                                        </span>
                                                    </div>
                                                )
                                            })}
                                    </div>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <div className='summer-break-planner__empty-result'>
                            No safe opening was found in this week.
                        </div>
                    )}

                    {(result.unassigned || []).length > 0 && (
                        <div className='summer-break-planner__unassigned'>
                            <strong>{result.unassigned.length} still need placement</strong>
                            {result.unassigned.map((item) => {
                                const student = studentByKey.get(item.studentKey)
                                return (
                                    <div key={item.studentKey}>
                                        <MdWarning size={14} aria-hidden='true' />
                                        <span>
                                            <strong>{student?.studentName || item.studentKey} — {instrumentNames(student)}</strong>
                                            <small>{item.reason}</small>
                                        </span>
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {(result.warnings || []).length > 0 && (
                        <details className='summer-break-planner__warnings'>
                            <summary>{result.warnings.length} existing-calendar notice{result.warnings.length === 1 ? '' : 's'}</summary>
                            {result.warnings.map((warning) => <p key={warning}>{warning}</p>)}
                        </details>
                    )}

                    {(result.errors || []).length > 0 && (
                        <div className='summer-break-planner__message is-error'>
                            <MdWarning size={18} aria-hidden='true' />
                            <span>{result.errors.join(' ')}</span>
                        </div>
                    )}

                    <button type='button' className='summer-break-planner__rerun' onClick={generatePreview}>
                        <MdRefresh size={15} /> Refresh preview
                    </button>
                </div>
            )}
        </section>
    )
}

export default SummerBreakReplacementPlanner
